/**
 * User-like interface that supports both AuthUser and User from @commander/utils
 */
type UserWithRoleCheck = {
  id: string;
  company_employee?: {
    id?: string;
    role?: string; // 'ADMIN', 'MANAGER', 'SALES_REP', 'VIEWER'
    status?: string; // 'JOINED', 'INVITED', 'DELETED'
    company?: {
      cro_admin_user_id?: string | null;
    };
  } | null;
};

/**
 * Checks if the given user is a company admin using the new role-based system.
 * A user is considered a company admin if their role is 'ADMIN' in their company_employee record.
 *
 * @param user - The authenticated user object (AuthUser or User)
 * @returns true if the user is a company admin, false otherwise
 */
export function isCompanyAdmin(user: UserWithRoleCheck | null | undefined): boolean {
  if (!user) {
    return false;
  }

  const userRole = user.company_employee?.role;
  const userStatus = user.company_employee?.status;

  // User must have ADMIN role and must be in JOINED status
  return userRole === 'ADMIN' && userStatus === 'JOINED';
}

/**
 * Checks if the given user is a company manager (ADMIN or MANAGER).
 * This includes both full admins and managers who can manage company data.
 *
 * @param user - The authenticated user object (AuthUser or User)
 * @returns true if the user is a company admin or manager, false otherwise
 */
export function isCompanyManager(user: UserWithRoleCheck | null | undefined): boolean {
  if (!user) {
    return false;
  }

  const userRole = user.company_employee?.role;
  const userStatus = user.company_employee?.status;

  // User must have ADMIN or MANAGER role and must be in JOINED status
  return ['ADMIN', 'MANAGER'].includes(userRole || '') && userStatus === 'JOINED';
}

/**
 * Checks if the given user is a sales rep or higher (ADMIN, MANAGER, or SALES_REP).
 * Excludes VIEWER and DRIVER. Mirrors the backend `validateSalesRepOrHigher` gate.
 *
 * @param user - The authenticated user object (AuthUser or User)
 * @returns true if the user's role is ADMIN/MANAGER/SALES_REP and JOINED, false otherwise
 */
export function isSalesRepOrHigher(user: UserWithRoleCheck | null | undefined): boolean {
  if (!user) {
    return false;
  }

  const userRole = user.company_employee?.role;
  const userStatus = user.company_employee?.status;

  return ['ADMIN', 'MANAGER', 'SALES_REP'].includes(userRole || '') && userStatus === 'JOINED';
}
